import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, X } from "lucide-react";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import type { Testimonial } from "../data/testimonials";

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

interface TestimonialVideoLightboxProps {
  testimonial: Testimonial | null;
  onClose: () => void;
}

/** Built on FacilityLightbox.tsx's focus-trapped dialog pattern — no testimonial has real video yet, so this stays unreachable in production data. */
export default function TestimonialVideoLightbox({ testimonial, onClose }: TestimonialVideoLightboxProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const open = testimonial !== null && testimonial.media !== null;

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => closeButtonRef.current?.focus(), 10);
      return () => clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && testimonial?.media && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.15 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-ocean-950/90 px-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${testimonial.name}'s video testimonial`}
            initial={reducedMotion ? false : { opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: reducedMotion ? 0 : 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="relative aspect-video bg-black">
              <video controls autoPlay playsInline aria-label={testimonial.media.posterAlt} className="h-full w-full">
                <track kind="captions" src={testimonial.media.captionsUrl} default />
              </video>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-4 p-5">
              <div>
                <p className="font-display text-sm font-semibold text-ocean-950">{testimonial.name}</p>
                <p className="text-xs text-ocean-800/50">{testimonial.city}</p>
              </div>
              <a
                href={testimonial.media.transcriptUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700"
              >
                <FileText className="h-3.5 w-3.5" />
                Read transcript
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
